import type { DiseaseHistoryState } from "./DiseaseHistoryContext";
import { DISEASE_CATEGORY_OPTIONS } from "./diseases/options";
import type { DiseaseCategoryType } from "./diseases/options";

// Generate route names for each disease category
export type DiseaseCategoryRoute = `Disease_${DiseaseCategoryType}`;

export type DiseaseHistoryFlowRoute =
  // Breast Testing
  | "BreastTesting"
  | "BreastTestingDetails"
  | "BreastConditions"
  | "BreastSymptomOther"
  // Disease Categories (one screen per category)
  | "DiseaseCategories"
  | DiseaseCategoryRoute
  | "DiseaseOther"
  | "DiseaseDetails"
  // Cancer
  | "CancerHistory"
  | "CancerDetails"
  | "CancerTreatments"
  | "CancerTreatmentDetails"
  // Complete
  | "DiseaseHistoryComplete";

/** All disease category route names in order */
export const DISEASE_CATEGORY_ROUTES: DiseaseCategoryRoute[] =
  DISEASE_CATEGORY_OPTIONS.map(
    (cat) => `Disease_${cat.id}` as DiseaseCategoryRoute,
  );

/** Extract category ID from a disease route name */
export function getCategoryFromRoute(
  routeName: string,
): DiseaseCategoryType | null {
  if (!routeName.startsWith("Disease_")) return null;
  return routeName.replace("Disease_", "") as DiseaseCategoryType;
}

export function buildDiseaseHistoryNavigationStack(
  state: DiseaseHistoryState,
  sex?: "AFAB" | "AMAB" | null,
): DiseaseHistoryFlowRoute[] {
  const isAMAB = sex === "AMAB";
  const stack: DiseaseHistoryFlowRoute[] = [];

  // Breast testing — AFAB only
  if (!isAMAB) {
    stack.push("BreastTesting");

    // Show details screen only if user selected tests
    if (state.hasBreastTestings === true && state.breastTestings.length > 0) {
      stack.push("BreastTestingDetails");
    }

    // Always show breast conditions screen
    stack.push("BreastConditions");

    // Show "other" text screen only if user selected "other" condition
    if (
      state.hasBreastConditions === true &&
      state.breastConditions.some((c) => c.condition === "other")
    ) {
      stack.push("BreastSymptomOther");
    }
  }

  // ── Disease Categories (one screen per category) ──────
  for (const route of DISEASE_CATEGORY_ROUTES) {
    stack.push(route);
  }

  // Show disease details if any diseases are selected
  const hasAnyDiseases = state.diseases.length > 0;
  if (hasAnyDiseases) {
    stack.push("DiseaseDetails");
  }

  // ── Cancer History ──────────────────────────────────────
  stack.push("CancerHistory");

  // Show cancer details if user said yes
  if (state.hasCancerHistory === true) {
    stack.push("CancerDetails");

    // Always show treatment selection after cancer details
    stack.push("CancerTreatments");

    // Show treatment details if any cancer has treatments selected
    const hasAnyTreatments = state.cancers.some((c) => c.treatments.length > 0);
    if (hasAnyTreatments) {
      stack.push("CancerTreatmentDetails");
    }
  }

  stack.push("DiseaseHistoryComplete");
  return stack;
}
